import { useReactiveVar } from '@apollo/client';
import type React from 'react';
import { useEffect } from 'react';
import { getPersistedDrawerWidth, persistDrawerWidth } from 'src/helpers/drawerWidthPersistence';
import viewStateVar from 'src/store/viewState';

const KEYBOARD_STEP = 10;

type WidthKey = 'leftDrawerWidth' | 'rightDrawerWidth';

const SIDE_TO_WIDTH_KEY: Record<'left' | 'right', WidthKey> = {
  left: 'leftDrawerWidth',
  right: 'rightDrawerWidth',
};

interface DrawerResizeConfig {
  side: 'left' | 'right';
  min: number;
  max: number;
  defaultWidth: number;
  storageKey?: string;
}

interface ResizeHandleProps {
  role: 'separator';
  'aria-orientation': 'vertical';
  'aria-valuemin': number;
  'aria-valuemax': number;
  'aria-valuenow': number;
  tabIndex: number;
  style: { touchAction: 'none' };
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

interface DrawerResizeResult {
  width: number;
  resizeHandleProps: ResizeHandleProps;
}

const useDrawerResize = (config: DrawerResizeConfig): DrawerResizeResult => {
  const { side, min, max, defaultWidth, storageKey } = config;
  const widthKey = SIDE_TO_WIDTH_KEY[side];
  const viewState = useReactiveVar(viewStateVar);
  const width = viewState[widthKey];

  useEffect(() => {
    if (!storageKey) return;
    const persisted = getPersistedDrawerWidth(storageKey, defaultWidth, min, max);
    if (persisted !== viewStateVar()[widthKey]) {
      viewStateVar({ ...viewStateVar(), [widthKey]: persisted });
    }
  }, [storageKey, defaultWidth, min, max, widthKey]);

  const clampWidth = (raw: number): number => Math.min(max, Math.max(min, raw));

  const calcWidth = (e: React.PointerEvent): number => {
    if (side === 'left') {
      return clampWidth(e.clientX - document.body.offsetLeft);
    }
    return clampWidth(document.body.offsetWidth - e.clientX - document.body.offsetLeft);
  };

  const handlePointerDown = (e: React.PointerEvent): void => {
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent): void => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const newWidth = calcWidth(e);
    viewStateVar({ ...viewStateVar(), [widthKey]: newWidth });
  };

  const handlePointerUp = (e: React.PointerEvent): void => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (storageKey) {
      persistDrawerWidth(storageKey, viewStateVar()[widthKey]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    const growKey = side === 'left' ? 'ArrowRight' : 'ArrowLeft';
    const shrinkKey = side === 'left' ? 'ArrowLeft' : 'ArrowRight';

    let newWidth: number | null = null;
    if (e.key === growKey) {
      newWidth = clampWidth(width + KEYBOARD_STEP);
    } else if (e.key === shrinkKey) {
      newWidth = clampWidth(width - KEYBOARD_STEP);
    }

    if (newWidth !== null && newWidth !== width) {
      e.preventDefault();
      viewStateVar({ ...viewStateVar(), [widthKey]: newWidth });
      if (storageKey) {
        persistDrawerWidth(storageKey, newWidth);
      }
    }
  };

  const resizeHandleProps: ResizeHandleProps = {
    role: 'separator',
    'aria-orientation': 'vertical',
    'aria-valuemin': min,
    'aria-valuemax': max,
    'aria-valuenow': width,
    tabIndex: 0,
    style: { touchAction: 'none' },
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onKeyDown: handleKeyDown,
  };

  return { width, resizeHandleProps };
};

export default useDrawerResize;
